/**
 * P2-02 · Mega-menu panel — presentational (no state, jsdom-testable). One
 * open panel per L1 trigger: child-category columns (L2 heading is a link,
 * L3 links beneath — P2-DB03 r2 fixed depth), a "View all" row, and the
 * optional featured tile (image via StoreImage, or a token-gradient text
 * tile when the brand configured no/unresolvable media). Desktop-only by
 * construction — the drawer never renders this.
 */
import { StoreImage } from "~/components/ui/store-image";
import { LocaleLink } from "~/i18n/provider";
import type { NavItem } from "~/server/nav/compose-nav-tree";

export function MegaMenuPanel({
  id,
  item,
  onItemClick,
}: {
  id: string;
  item: Extract<NavItem, { kind: "category" }>;
  onItemClick: (
    type: "category" | "view_all",
    href: string,
    label: string,
  ) => void;
}) {
  return (
    <div
      id={id}
      // z-40: above the nav scrim (z-30) within the header's stacking context.
      className="absolute left-0 right-0 top-full z-40 border-b border-border bg-surface shadow-[0_24px_40px_-24px_rgb(0_0_0/0.35)]"
    >
      <div className="mx-auto flex max-w-6xl gap-8 px-4 py-6">
        <div className="min-w-0 flex-1">
          <div className="grid gap-8 lg:grid-cols-3">
            {item.columns.map((column) => (
              <div key={column.href} className="text-sm">
                <LocaleLink
                  href={column.href}
                  onClick={() =>
                    onItemClick("category", column.href, column.label)
                  }
                  className="mb-3 inline-block text-xs font-semibold uppercase tracking-wide text-fg-muted hover:text-fg"
                >
                  {column.label}
                </LocaleLink>
                <ul className="flex flex-col gap-2">
                  {column.children.map((leaf) => (
                    <li key={leaf.href}>
                      <LocaleLink
                        href={leaf.href}
                        onClick={() =>
                          onItemClick("category", leaf.href, leaf.label)
                        }
                        className="text-fg hover:text-primary"
                      >
                        {leaf.label}
                      </LocaleLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Own row, once — not per column (P2-DB03 r2 placement decision). */}
          <div className="mt-6 text-sm">
            <LocaleLink
              href={item.viewAllHref}
              onClick={() =>
                onItemClick("view_all", item.viewAllHref, item.label)
              }
              className="font-semibold text-primary hover:underline"
            >
              View all {item.label} →
            </LocaleLink>
          </div>
        </div>

        {item.featured && (
          <LocaleLink
            href={item.featured.href}
            onClick={() =>
              onItemClick("category", item.featured!.href, item.featured!.title)
            }
            className="relative block w-72 shrink-0 self-start overflow-hidden rounded-lg border border-border"
          >
            <span className="relative flex aspect-[4/3] flex-col justify-end p-4">
              {item.featured.image ? (
                <StoreImage
                  src={item.featured.image.url}
                  alt=""
                  fill
                  preset="card"
                  blurDataUrl={item.featured.image.blurDataUrl ?? undefined}
                  className="absolute inset-0 -z-10 object-cover"
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/20 via-surface-2 to-surface"
                />
              )}
              <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
                Featured
              </span>
              <span className="font-heading text-lg font-semibold text-fg">
                {item.featured.title}
              </span>
              {item.featured.subtitle && (
                <span className="text-sm text-fg-muted">
                  {item.featured.subtitle}
                </span>
              )}
            </span>
          </LocaleLink>
        )}
      </div>
    </div>
  );
}
